// src/app/time-attendance/services/violations.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import {
  TimeViolation,
  TimeViolationsResponse,
  ViolationFilterValues,
  ViolationFilterValuesResponse,
  ViolationListFilters,
} from '../../models/violations.model';

@Injectable({ providedIn: 'root' })
export class ViolationsService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  getViolations(
    pageNumber: number = 1,
    pageSize: number = 25,
    orderBy: string = 'logTimeDesc',
    filters?: ViolationListFilters,
  ): Observable<TimeViolationsResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize))
      .set('orderBy', orderBy);

    const searchTerm = String(filters?.searchTerm ?? '').trim();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (filters?.companyId != null) {
      params = params.set('companyIds', String(filters.companyId));
    }

    if (filters?.departmentId != null) {
      params = params.set('departmentIds', String(filters.departmentId));
    }

    return this.http
      .get<any>(`${this.baseUrl}/api/time-violations`, { params })
      .pipe(
        map((res): TimeViolationsResponse => ({
          status: res.status ?? '',
          totalCount: Number(res.totalCount ?? 0),
          totalPages: Number(res.totalPages ?? 1),
          currentPage: Number(res.currentPage ?? 1),
          pageSize: Number(res.pageSize ?? pageSize),
          data: (res.data ?? []).map((row: any) => this.mapViolation(row)),
        })),
        catchError((err) => {
          console.error('Error fetching violations:', err);
          return of(null);
        }),
      );
  }

  getFilterValues(): Observable<ViolationFilterValues> {
    return this.http
      .get<ViolationFilterValuesResponse>(`${this.baseUrl}/api/users/filter-values`)
      .pipe(
        map((res) => ({
          companies: (res.data?.companies ?? []).map((company) => ({
            id: company.id,
            name: company.name,
            code: company.code,
          })),
          departments: (res.data?.departments ?? []).map((department) => ({
            id: department.id,
            name: department.name,
            code : department.code,
          })),
        })),
        catchError((err) => {
          console.error('Error fetching violation filter values:', err);
          return of({
            companies: [],
            departments: [],
          });
        }),
      );
  }

  toggleViolationApproval(id: number): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/time-violations/${id}/toggle-approval`, {})
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error toggling violation approval:', err);
          return of(false);
        }),
      );
  }

  private mapViolation(row: any): TimeViolation {
    return {
      id: Number(row.id ?? 0),
      employee: row.employee
        ? {
            id: Number(row.employee.id ?? 0),
            firstName: row.employee.firstName ?? '',
            lastName: row.employee.lastName ?? '',
            employeeNumber: row.employee.employeeNumber ?? null,
            rfid: row.employee.rfid ?? null,
            reportTo: row.employee.reportTo
              ? {
                  id: Number(row.employee.reportTo.id ?? 0),
                  firstName: row.employee.reportTo.firstName ?? '',
                  lastName: row.employee.reportTo.lastName ?? '',
                  employeeNumber: row.employee.reportTo.employeeNumber ?? null,
                  rfid: row.employee.reportTo.rfid ?? null,
                  reportTo: null,
                }
              : null,
          }
        : null,
      logTime: row.logTime ?? '',
      logType: row.logType ?? '',
      schedule: row.schedule
        ? {
            localStartTime: row.schedule.localStartTime ?? null,
            localEndTime: row.schedule.localEndTime ?? null,
          }
        : null,
      violation: row.violation
        ? {
            type: row.violation.type ?? '',
            isApproved: !!row.violation.isApproved,
            verifiedAt: row.violation.verifiedAt ?? null,
            verifiedBy: row.violation.verifiedBy
              ? {
                  id: Number(row.violation.verifiedBy.id ?? 0),
                  firstName: row.violation.verifiedBy.firstName ?? '',
                  lastName: row.violation.verifiedBy.lastName ?? '',
                  employeeNumber: row.violation.verifiedBy.employeeNumber ?? null,
                  rfid: row.violation.verifiedBy.rfid ?? null,
                }
              : null,
          }
        : null,
    };
  }
}